import NotificationsActive from '@mui/icons-material/NotificationsActive'
import { Alert, AlertTitle, Button, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'preact/hooks'
import type { PushKey } from '../api/generated/PushKey'
import { useAction } from '../api/useAction'
import { useApi } from '../api/useApi'
import { usePromptTurn } from '../promptTurn'
import { pushStatus, subscribe } from './push'

/** Asks to enable notifications on this device, and keeps its subscription saved once allowed. */
export function PushSetup() {
  const publicKey = useApi<PushKey>('/push/key').data?.public_key
  const [status, setStatus] = useState(pushStatus)
  const { error, pending, run } = useAction()

  // Saved again on every visit: the device may have changed account or subscription
  useEffect(() => {
    if (publicKey && pushStatus() === 'granted') subscribe(publicKey).catch(() => {})
  }, [publicKey])

  const turn = usePromptTurn(
    'push',
    !!publicKey && status !== 'unsupported' && (status !== 'granted' || !!error),
  )

  if (!turn || !publicKey) return null
  if (status === 'denied')
    return (
      <Alert severity="info">
        Notifications bloquées sur cet appareil : vos rappels et messages arriveront par SMS ou
        email. Pour les recevoir ici, autorisez les notifications dans les réglages du navigateur.
      </Alert>
    )

  const ask = () =>
    run(async () => {
      const permission = await Notification.requestPermission()
      setStatus(permission)
      if (permission === 'granted') await subscribe(publicKey)
    })

  return (
    <Alert severity="info" icon={<NotificationsActive />}>
      <AlertTitle>Activez les notifications</AlertTitle>
      <Typography variant="body2">
        Pour recevoir ici les rappels de vos créneaux et les messages ; sinon, ils arrivent par SMS
        ou email.
      </Typography>
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
      <Stack direction="row" sx={{ mt: 1 }}>
        <Button variant="contained" size="small" onClick={ask} loading={pending}>
          Activer
        </Button>
      </Stack>
    </Alert>
  )
}
